import React, { useState, useEffect, useRef } from 'react';
import { Tag, Check, X } from 'lucide-react';

interface QuickTagInputProps {
  onTagAdd: (tagName: string) => void;
  onCancel: () => void;
  suggestedTags?: string[];
  autoFocus?: boolean;
}

export const QuickTagInput: React.FC<QuickTagInputProps> = ({
  onTagAdd,
  onCancel,
  suggestedTags = ['USPS', 'FedEx', 'UPS', 'Amazon'],
  autoFocus = true
}) => {
  const [tagName, setTagName] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = () => {
    const trimmed = tagName.trim();
    if (trimmed) {
      onTagAdd(trimmed);
      setTagName('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const filteredSuggestions = suggestedTags.filter(tag =>
    tag.toLowerCase().includes(tagName.toLowerCase())
  );

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px',
        background: 'white',
        border: '2px solid #3b82f6',
        borderRadius: '6px',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Tag size={12} style={{ color: '#3b82f6', marginLeft: '4px' }} />
      <input
        ref={inputRef}
        type="text"
        value={tagName}
        onChange={(e) => {
          setTagName(e.target.value);
          setShowSuggestions(e.target.value.length > 0);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowSuggestions(tagName.length > 0)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder="Type tag..."
        style={{
          border: 'none',
          outline: 'none',
          padding: '4px 6px',
          fontSize: '0.75rem',
          width: '100px',
          fontWeight: '600'
        }}
      />

      {/* Quick suggestions */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 10000,
            minWidth: '120px'
          }}
        >
          {filteredSuggestions.slice(0, 4).map(tag => (
            <button
              key={tag}
              onMouseDown={(e) => {
                e.preventDefault();
                setTagName(tag);
                handleSubmit();
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '6px 12px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: '#374151'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!tagName.trim()}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
          border: 'none',
          background: tagName.trim() ? '#10b981' : '#e5e7eb',
          borderRadius: '4px',
          cursor: tagName.trim() ? 'pointer' : 'not-allowed',
          transition: 'background 0.2s'
        }}
        title="Add tag (Enter)"
      >
        <Check size={12} style={{ color: tagName.trim() ? 'white' : '#9ca3af' }} />
      </button>

      <button
        onClick={onCancel}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#6b7280'
        }}
        title="Cancel (Esc)"
      >
        <X size={12} />
      </button>
    </div>
  );
};
